if (!customElements.get('cp-inline-card')) {
  customElements.define('cp-inline-card', class extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
      this.modal = this.querySelector('cp-quick-add-modal') || document.getElementById(this.dataset.modalId);
      this.selects = [...this.modal.querySelectorAll('[data-option-index]')];
      this.form = this.modal.querySelector('form');
      this.opener = this.querySelector('.cp-quick-open');
      this.opener.addEventListener('click', () => {
        if (this.selects.length) this.modal.show(this.opener);
        else this.form.requestSubmit();
      }, { signal: this.abort.signal });
      this.input = this.form.querySelector('[name="id"]');
      this.button = this.form.querySelector('[type="submit"]');
      this.label = this.button.querySelector('span');
      this.status = this.modal.querySelector('.cp-inline-status');
      this.price = this.modal.querySelector('.cp-inline-price');
      this.basePrice = this.price.innerHTML;
      this.saleBadges = [...this.querySelectorAll('.cp-sale-badge')].map(node => ({node, text: node.textContent, hidden: node.classList.contains('hidden')}));
      this.modalImage = this.modal.querySelector('.cp-quick-image');
      this.baseModalImage = {src: this.modalImage.src, srcset: this.modalImage.srcset, alt: this.modalImage.alt};
      this.gallery = this.querySelector('.cp-inline-gallery');
      this.photos = [...this.querySelectorAll('.cp-inline-photo')];
      this.badgeResize = new ResizeObserver(() => this.alignSaleBadge());
      if (this.gallery) this.badgeResize.observe(this.gallery);
      this.gallery?.addEventListener('load', () => this.alignSaleBadge(), { capture: true, signal: this.abort.signal });
      this.gallery?.addEventListener('scroll', () => {
        clearTimeout(this.badgeScrollTimer);
        this.badgeScrollTimer = setTimeout(() => this.alignSaleBadge(), 120);
      }, { passive: true, signal: this.abort.signal });
      this.colorIndex = Number(this.dataset.colorIndex);
      this.lastColor = null;
      this.gallery?.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        event.stopPropagation();
        this.gallery.scrollBy({left: this.gallery.clientWidth * (event.key === 'ArrowRight' ? 1 : -1), behavior: this.motion()});
      }, { signal: this.abort.signal });
      this.modal.addEventListener('change', () => this.update(), { signal: this.abort.signal });
      // Dawn displays cart failures inline; also consume the analytics promise rejection.
      this.modal.addEventListener('shopify:cart:lines-update', event => {
        event.promise?.catch(() => {});
      }, { signal: this.abort.signal });
      // Validate before Dawn's product-form handler, including programmatic submits.
      this.form.addEventListener('submit', e => {
        if (!this.selected?.available || this.selects.some(s => !s.value)) {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.status.textContent = this.selected ? 'Cette combinaison est épuisée.' : 'Sélectionnez une combinaison disponible.';
        }
      }, { capture: true, signal: this.abort.signal });
      this.update();
    }
    update() {
      const values = this.selects.map(s => s.value);
      this.selected = this.selects.length ? this.variants.find(v => v.options.every((o, i) => o === values[i])) : this.variants[0];
      const v = this.selected;
      const ready = Boolean(v?.available);
      this.input.value = v ? String(v.id) : '';
      this.input.disabled = !ready;
      this.button.disabled = !ready;
      this.button.setAttribute('aria-disabled', String(!ready));
      this.label.classList.remove('hidden');
      this.label.textContent = ready ? window.variantStrings.addToCart : (v ? window.variantStrings.soldOut : window.variantStrings.addToCart);
      this.status.textContent = ready ? '' : (values.some(x => !x) ? 'Sélectionnez vos options.' : (v ? 'Cette combinaison est épuisée.' : 'Cette combinaison n’est pas disponible.'));
      this.selects.forEach((select, index) => [...select.options].forEach(option => {
        if (!option.value) return;
        const possible = this.variants.some(candidate => candidate.available && candidate.options[index] === option.value && candidate.options.every((value, i) => i >= index || !values[i] || value === values[i]));
        option.disabled = !possible;
      }));
      if (v) {
        const template = [...this.querySelectorAll('template[data-price-key]')].find(t => t.dataset.priceKey === v.priceKey);
        if (template) this.price.replaceChildren(template.content.cloneNode(true));
        this.form.querySelector('[name="quantity"]').value = String(Math.max(v.minimum, v.increment, 1));
      } else if (values.some(value => !value)) {
        this.price.innerHTML = this.basePrice;
      } else {
        this.price.replaceChildren();
      }
      this.saleBadges.forEach(({node, text, hidden}) => {
        node.textContent = v ? (v.savingLabel || '') : text;
        node.classList.toggle('hidden', v ? !v.savingLabel : hidden);
      });
      const color = values[this.colorIndex] || '';
      if (color !== this.lastColor) {
        this.lastColor = color;
        // Only Shopify's explicit variant/image associations establish colour membership.
        const associated = this.variants.filter(candidate => candidate.options[this.colorIndex] === color && candidate.image);
        const preferred = associated.find(candidate => candidate.available)?.image || associated[0]?.image;
        const ids = new Set(associated.map(candidate => String(candidate.image.id)));
        const allAssigned = new Set(this.variants.filter(candidate => candidate.image).map(candidate => String(candidate.image.id)));
        const neutral = this.photos.filter(photo => !allAssigned.has(photo.dataset.imageId));
        const matched = this.photos.filter(photo => ids.has(photo.dataset.imageId));
        const remaining = this.photos.filter(photo => !ids.has(photo.dataset.imageId) && !neutral.includes(photo));
        const ordered = color ? [...matched, ...neutral, ...remaining] : this.photos;
        if (preferred) {
          this.modalImage.src = preferred.src;
          this.modalImage.removeAttribute('srcset');
          this.modalImage.alt = preferred.alt;
        } else {
          const fallback = ordered[0]?.querySelector('img');
          this.modalImage.src = fallback?.src || this.baseModalImage.src;
          this.modalImage.srcset = fallback?.srcset || this.baseModalImage.srcset;
          this.modalImage.alt = fallback?.alt || this.baseModalImage.alt;
        }
        if (this.gallery) {
          this.gallery.replaceChildren(...ordered);
          const target = preferred ? ordered.find(photo => photo.dataset.imageId === String(preferred.id)) : ordered[0];
          this.gallery.scrollTo({ left: target?.offsetLeft || 0, behavior: 'instant' });
        }
      }
      this.alignSaleBadge();
      this.modal.querySelector('product-form')?.handleErrorMessage?.();
    }
    alignSaleBadge() {
      if (!this.gallery?.clientWidth) return;
      const photo = this.gallery.children[Math.round(this.gallery.scrollLeft / this.gallery.clientWidth)];
      const image = photo?.querySelector('img');
      if (!image) return;
      const ratio = (image.naturalWidth || Number(image.getAttribute('width'))) / (image.naturalHeight || Number(image.getAttribute('height')));
      const rect = image.getBoundingClientRect();
      const inset = getComputedStyle(image).objectFit === 'contain' && ratio > 0 ? Math.max(0, (rect.width - rect.height * ratio) / 2) : 0;
      this.gallery.closest('.card__inner').style.setProperty('--cp-sale-photo-offset', inset + 'px');
    }
    motion() { return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'; }
    disconnectedCallback() {
      this.abort?.abort();
      this.badgeResize?.disconnect();
      clearTimeout(this.badgeScrollTimer);
      if (this.modal?.hasAttribute('open')) this.modal.hide();
      this.modal?.remove();
    }
  });
}

// Reuse Dawn's focus trap, Escape/backdrop dismissal and scroll lock.
if (!customElements.get('cp-quick-add-modal')) {
  customElements.define('cp-quick-add-modal', class extends ModalDialog {
    hide(preventFocus = false) {
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      cart?.setActiveElement(this.openedBy);
      const opener = this.openedBy;
      if (preventFocus) this.openedBy = null;
      super.hide();
      this.openedBy = opener;
    }
  });
}
